export type AmountSeparators = {
  decimal: string;
  group: string;
};

/**
 * Целое число с необязательными группами через "." или ",".
 * Несколько разделителей допускаем - последний считаем десятичным.
 */
const DECIMAL_AMOUNT = /^[+-]?\d+([.,]\d*)*$/;

/** Разделители, кроме последнего: за таким в строке есть еще один */
const NOT_LAST_SEPARATOR = /[.,](?=.*[.,])/g;

/**
 * Разряды тысяч в целой части.
 * Разделитель берем у локали: в ru-RU и ky-KG это неразрывный пробел, в en-US - запятая.
 */
const groupIntegerPart = (value: string, group: string): string => {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, group);
};

/**
 * Разделитель дроби и разряды тысяч.
 *
 * Цифры не меняются: ни округления, ни обрезки, ни добивки нулями - глубина
 * отображения остается такой, какой пришла.
 * Последняя "," или "." считается десятичным разделителем, остальные такие же
 * символы в целой части убираются.
 *
 * Смешение "," и "." в одной строке и не-десятичное число не обрабатываем -
 * возвращаем исходную строку.
 */
export const formatAmountString = (
  amount: string,
  separators: AmountSeparators,
): string => {
  // Обрезка по краям и удаление всех пробелов внутри, включая неразрывные
  const cleaned = amount.trim().replace(/\s/g, "");
  const isMixed = cleaned.includes(".") && cleaned.includes(",");

  if (isMixed || !DECIMAL_AMOUNT.test(cleaned)) {
    return amount;
  }

  const [integerPart, fractionPart] = cleaned
    .replace(NOT_LAST_SEPARATOR, "")
    .split(/[.,]/);

  const groupedIntegerPart = groupIntegerPart(integerPart, separators.group);

  return fractionPart
    ? `${groupedIntegerPart}${separators.decimal}${fractionPart}`
    : groupedIntegerPart;
};
